import React from 'react';
import { PropTypes } from 'prop-types';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';

import { Box, Checkbox, Grid, OutlinedInput, Typography } from '@material-ui/core';
import clientService from '../../../../services/ClientService';
import { toast } from 'react-hot-toast';

const useStyles = makeStyles(() => ({
  inputLabel: {
    color: 'rgb(107, 108, 114)',
    fontSize: '1.4rem',
    marginBottom: '4px',
    fontWeight: '800'
  }
}));

const accountingSchema = z
  .object({
    costPlusChecked: z.boolean(),
    markUpValue: z.preprocess(
      (val) => (val === '' || val === null || val === undefined ? null : Number(val)),
      z.number().nullable().optional()
    ),
    opsAllowanceApplicable: z.boolean()
  })
  .superRefine((data, ctx) => {
    if (
      data.costPlusChecked &&
      (data.markUpValue === null || data.markUpValue === undefined || isNaN(data.markUpValue))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Markup percentage is required if cost plus is checked',
        path: ['markUpValue']
      });
    }
  });

const Accounting = ({ row, onCancelChanges, setCustomerTabIndex, onUpdateRow }) => {
  const classes = useStyles();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, dirtyFields }
  } = useForm({
    resolver: zodResolver(accountingSchema),
    defaultValues: {
      costPlusChecked: row.BillableStatus || false,
      markUpValue: row.MarkUpPercentage !== null && row.MarkUpPercentage !== undefined ? row.MarkUpPercentage : '',
      opsAllowanceApplicable: row.opsAllowanceApplicable || false
    },
    mode: 'onChange'
  });

  const costPlusChecked = watch('costPlusChecked');

  const onSubmit = (data) => {
    const editClientPayload = {
      editId: row.id
    };

    let hasChanges = false;

    // If costPlusChecked changed
    if (dirtyFields.costPlusChecked) {
      editClientPayload.costPlusChecked = data.costPlusChecked;
      editClientPayload.BillableStatus = data.costPlusChecked;
      hasChanges = true;
    }

    // If markUpValue changed OR if costPlusChecked was toggled (which clears/changes markup status)
    if (dirtyFields.markUpValue || dirtyFields.costPlusChecked) {
      editClientPayload.MarkUpPercentage = data.costPlusChecked ? data.markUpValue : null;
      hasChanges = true;
    }

    // If opsAllowanceApplicable changed
    if (dirtyFields.opsAllowanceApplicable) {
      editClientPayload.opsAllowanceApplicable = data.opsAllowanceApplicable;
      hasChanges = true;
    }

    if (!hasChanges) {
      toast.error('No changes to save');
      return;
    }

    clientService
      .editClient(editClientPayload)
      .then(() => {
        toast.success('Successfully updated client');
        if (onUpdateRow) {
          const updatePayload = { id: row.id };
          if ('costPlusChecked' in editClientPayload) {
            updatePayload.costPlusChecked = editClientPayload.costPlusChecked;
            updatePayload.BillableStatus = editClientPayload.BillableStatus;
          }
          if ('MarkUpPercentage' in editClientPayload) {
            updatePayload.MarkUpPercentage = editClientPayload.MarkUpPercentage;
          }
          if ('opsAllowanceApplicable' in editClientPayload) {
            updatePayload.opsAllowanceApplicable = editClientPayload.opsAllowanceApplicable;
          }
          onUpdateRow(updatePayload);
        }
        onCancelChanges();
        setCustomerTabIndex(0);
      })
      .catch((error) => {
        toast.error(error?.message || 'Could not edit customer.');
      });
  };

  return (
    <div style={{ height: '700px' }}>
      <DialogTitle id="form-dialog-title">Billable/Variation</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item xs={12} sm={4} className="tw-flex tw-items-center">
            <Controller
              name="costPlusChecked"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="costPlusChecked"
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (!e.target.checked) {
                      setValue('markUpValue', '');
                    }
                  }}
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                />
              )}
            />
            <InputLabel shrink htmlFor="costPlusChecked" className={classes.inputLabel}>
              Cost plus
            </InputLabel>
          </Grid>
          {costPlusChecked && (
            <div style={{ paddingLeft: '4%', marginBottom: '1rem' }}>
              <Typography component="div" m={1} style={{ marginBottom: '0.6rem' }}>
                <Box className="tw-text-sm tw-font-medium tw-text-[#6b6c72]"> Mark up(%)</Box>
              </Typography>
              <Controller
                name="markUpValue"
                control={control}
                render={({ field }) => (
                  <>
                    <OutlinedInput
                      id="markUpValue"
                      margin="dense"
                      value={field.value}
                      onChange={field.onChange}
                      type="number"
                      fullWidth={true}
                      error={!!errors.markUpValue}
                    />
                    {errors.markUpValue && (
                      <p
                        style={{
                          color: 'red',
                          marginTop: '5px',
                          fontSize: '0.8rem',
                          lineHeight: '1',
                          marginBottom: '0px'
                        }}
                      >
                        {errors.markUpValue.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          )}
          <div style={{ marginTop: '1rem' }}>
            <Controller
              name="opsAllowanceApplicable"
              control={control}
              render={({ field }) => (
                <>
                  <Checkbox
                    id="opsAllowanceApplicable"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                  <span
                    style={{
                      fontWeight: '800',
                      color: 'rgb(107, 108, 114)',
                      fontSize: '1rem'
                    }}
                  >
                    Ops Allowance Applicable
                  </span>
                </>
              )}
            />
          </div>
        </form>
      </DialogContent>
      <DialogActions style={{ padding: '0 23px 16px 0' }}>
        <Button onClick={onCancelChanges} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </div>
  );
};

export default Accounting;

Accounting.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string,
    DisplayName: PropTypes.string,
    Email: PropTypes.string,
    Phone: PropTypes.string,
    BillableStatus: PropTypes.bool,
    MarkUpPercentage: PropTypes.number,
    costPlusChecked: PropTypes.bool,
    opsAllowanceApplicable: PropTypes.bool
  }),
  onCancelChanges: PropTypes.func,
  setCustomerTabIndex: PropTypes.func,
  onUpdateRow: PropTypes.func
};
